import { MetadataEntityData } from '../../../metadata/entity/decorators/model/metadata.entity.data';
import { MetadataEntityFieldData } from '../../../metadata/entity/decorators/model/metadata.entity.field.data';
import { EntityView } from './entity.view';
import { EntityViewDisplay } from './enums';

/**
 * How a group row is decorated when the board groups by a field (e.g. stages by jobNumber).
 * Title + elevated fields are read from a sample leaf row under the group node.
 * Fields need not be visible columns but must be projected onto the row.
 *
 * Same shape as MetadataEntityFieldData.groupHeaderDefaults (view override).
 */
export interface EntityViewGroupHeader {
  /** Main label field (defaults to groupBy). */
  titleField?: string;
  /** Additional fields shown after the title (customer, site, …). */
  fields?: string[];
  /** Show the leaf count. Defaults to true. */
  showCount?: boolean;
}

/** Fully resolved group-header configuration with metadata defaults and fallback values applied. */
export interface ResolvedViewGroupHeader {
  /** Active grouping field path. */
  groupBy: string;
  /** Resolved group-title field path. */
  titleField: string;
  /** Resolved elevated field paths. */
  fields: string[];
  /** Whether the grouped leaf count is shown. */
  showCount: boolean;
}

/**
 * Resolve group header chrome for the active view grouping (table/list groupBy, or the kanban row).
 *
 * Precedence:
 * 1. per-view override — `view.kanban.rowHeader` for the kanban row, `view.table.groupHeader` otherwise
 * 2. metadata field.groupHeaderDefaults on the grouping field
 * 3. minimal fallback: title = groupBy, no elevated fields
 */
export function resolveViewGroupHeader(
  view: EntityView | null | undefined,
  metadata?: MetadataEntityData | null,
  field?: string | null,
): ResolvedViewGroupHeader | null {

  const kanbanRow = isSet(view?.kanban?.row) ? String(view?.kanban?.row) : undefined;
  const tableGroup = view?.table?.groupBy ?? view?.list?.groupBy;
  // A Kanban view has no table groupBy, so the row IS the active grouping when no field is named.
  const primaryGroup = view?.display === EntityViewDisplay.Kanban ? (tableGroup ?? kanbanRow) : tableGroup;
  const groupByRaw = field ?? primaryGroup;
  if (!isSet(groupByRaw)) return null;

  const groupBy = String(groupByRaw);
  // The table override owns the table/list groupBy even when the kanban row names the same field,
  // so a Job group row and a Job swimlane on one view keep their own chrome.
  const viewOverride = isSet(tableGroup) && groupBy === String(tableGroup)
    ? view?.table?.groupHeader
    : (groupBy === kanbanRow ? view?.kanban?.rowHeader : undefined);
  const metadataDefaults = findGroupHeaderDefaultsFromMetadata(metadata, groupBy);

  // Merge: view override fills gaps over metadata defaults over key fallback
  const titleField = String(
    viewOverride?.titleField
    ?? metadataDefaults?.titleField
    ?? groupBy,
  );
  const fields = [
    ...(viewOverride?.fields
      ?? metadataDefaults?.fields
      ?? []),
  ].map(String);
  const showCount = viewOverride?.showCount
    ?? metadataDefaults?.showCount
    ?? true;

  return { groupBy, titleField, fields, showCount };
}

/**
 * Field paths that must be projected when a group header is active (title + elevated fields).
 *
 * Covers the kanban row independently of `display`: the same stored view is read by the server
 * projection before any display is chosen, so a row header's `customer`/`site` must be projected
 * whenever `kanban.row` is set, or the swimlane renders with nothing but the job number.
 */
export function collectGroupHeaderFieldIds(
  view: EntityView | null | undefined,
  metadata?: MetadataEntityData | null,
): string[] {

  const primary = resolveViewGroupHeader(view, metadata);
  const secondary = isSet(view?.table?.subGroupBy) ? resolveViewGroupHeader(view, metadata, String(view?.table?.subGroupBy)) : null;
  const kanbanRow = isSet(view?.kanban?.row) ? resolveViewGroupHeader(view, metadata, String(view?.kanban?.row)) : null;

  return [...new Set([primary, secondary, kanbanRow].flatMap(def => def ? [def.titleField, ...def.fields] : []).filter(Boolean))];
}

/** A grouping field is absent when unset, empty, or the explicit 'none' sentinel views persist. */
function isSet(value: unknown): value is string {

  return value != null && value !== '' && value !== 'none';
}

/**
 * Look up groupHeaderDefaults on the field matching groupBy (path, name, or attributeFilterName).
 * Walks primary fields and secondaryFilter chain with absolute path prefixes.
 */
export function findGroupHeaderDefaultsFromMetadata(
  metadata: MetadataEntityData | null | undefined,
  groupBy: string,
): EntityViewGroupHeader | null {

  if (!metadata?.fields?.length || !groupBy) return null;

  const field = findFieldInMetadataTree(metadata, groupBy);
  if (!field?.groupHeaderDefaults) return null;

  const defaults = field.groupHeaderDefaults;
  return {
    titleField: defaults.titleField,
    fields: defaults.fields,
    showCount: defaults.showCount,
  };
}

function findFieldInMetadataTree(
  metadata: MetadataEntityData,
  groupBy: string,
  pathPrefix = '',
): MetadataEntityFieldData | null {

  for (const field of metadata.fields ?? []) {
    const name = field.name;
    const path = field.path ?? name;
    const absolute = pathPrefix ? `${pathPrefix}.${path}` : path;
    const absoluteName = pathPrefix ? `${pathPrefix}.${name}` : name;

    if (
      groupBy === absolute
      || groupBy === absoluteName
      || groupBy === path
      || groupBy === name
      || groupBy === field.attributeFilterName
      || (pathPrefix === '' && groupBy === '_id' && (name === '_id' || field.identifier))
    ) {
      return field;
    }
  }

  // Walk secondaryFilter chain (Job → Stage → Code) so groupBy can be a secondary path
  const secondary = metadata.secondaryFilter;
  if (secondary?.arrayPath) {
    const childMeta = secondary.metadata
      ?? (metadata.fields?.find(f => f.name === secondary.arrayPath || f.path === secondary.arrayPath)?.typeData as MetadataEntityData | undefined);
    if (childMeta?.fields?.length) {
      const prefix = pathPrefix ? `${pathPrefix}.${secondary.arrayPath}` : secondary.arrayPath;
      // Direct match on this array path is not a groupable field — recurse into child entity fields
      const found = findFieldInMetadataTree(childMeta, groupBy, prefix);
      if (found) return found;
      // Also allow bare names on child when groupBy is unprefixed (unusual for secondary)
      const bare = findFieldInMetadataTree(childMeta, groupBy, '');
      if (bare) return bare;
    }
  }

  return null;
}
